import { cn } from "../../lib/utils.mjs";
import { focusRing } from "../../lib/focus.mjs";
import { jsx, jsxs } from "react/jsx-runtime";
import { faChevronRight } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
//#region src/components/ui/disclosure.tsx
/**
* Spec 204 DEC-13 (CON-1304) — Disclosure: "show more without leaving".
*
* A styled native `<details>` / `<summary>`: no client JavaScript, it works
* in a server component and inside a plain `<form>`, the browser gives it
* the disclosure semantics (a button-like summary with an expanded state)
* and the find-in-page reveal, and a hash link to an id inside opens it.
* About seventy hand-styled `<details>` in the apps each drew the marker and
* the frame differently; this is the one.
*
* - `variant="inset"` (default) — a tier-3 inset (DESIGN.md §13): a 1px
*   border, `rounded-inset`, the summary as a padded row. For an advanced
*   section, a raw payload, a "How it works" block inside a panel.
* - `variant="plain"` — no frame: the summary is an inline control with
*   the chevron, the body follows under it. For a disclosure in running
*   text or inside a row that is already framed.
*
* The chevron turns a quarter on open; the turn is `motion-safe:` only.
* The summary carries the one focus ring. `defaultOpen` renders it open;
* the browser owns the state after that (pass `open` + `onToggle` to
* control it).
*/
function Disclosure({ summary, children, variant = "inset", defaultOpen, open, summaryClassName, contentClassName, className, ...props }) {
	const inset = variant === "inset";
	return /* @__PURE__ */ jsxs("details", {
		"data-disclosure": variant,
		open: open ?? defaultOpen,
		className: cn("group/disclosure", inset && "rounded-inset border border-border bg-background/60", className),
		...props,
		children: [/* @__PURE__ */ jsxs("summary", {
			className: cn("flex cursor-pointer list-none items-center gap-2 label-md font-medium text-text-heading-1 [&::-webkit-details-marker]:hidden", inset ? "rounded-inset px-4 py-3 hover:bg-accent/40" : "w-fit rounded-full py-1 pr-2", focusRing, summaryClassName),
			children: [/* @__PURE__ */ jsx(FontAwesomeIcon, {
				icon: faChevronRight,
				"aria-hidden": true,
				className: "h-3 w-3 shrink-0 text-muted-foreground motion-safe:transition-transform motion-safe:duration-[var(--duration-fast)] group-open/disclosure:rotate-90"
			}), /* @__PURE__ */ jsx("span", {
				className: "min-w-0 flex-1",
				children: summary
			})]
		}), /* @__PURE__ */ jsx("div", {
			className: cn("body-sm text-text-body", inset ? "px-4 pt-1 pb-4" : "pt-2 pl-5", contentClassName),
			children
		})]
	});
}
//#endregion
export { Disclosure };
